import React, { useState, useEffect } from "react";
import {
  Form,
  Select,
  Upload,
  Button,
  Modal,
  message,
  Divider,
  Row,
  Col,
  Space,
  Input,
} from "antd";
import { CameraOutlined } from "@ant-design/icons";
import { Api_Url } from "../../../config/config";

const { Option } = Select;
const { TextArea } = Input;

export const BranchOpeningModalAdd = ({ onClose, onSuccess }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [branches, setBranches] = useState([]);
  const [shutterImage, setShutterImage] = useState(null);
  const [cboApprovalImage, setCboApprovalImage] = useState(null);
  const [location, setLocation] = useState({ lat: null, lng: null });

  useEffect(() => {
    fetchBranches();
    getGeolocation();

    const now = new Date();
    const deadline = new Date();
    deadline.setHours(10, 0, 0, 0); // 10:00 AM

    // --- UPDATED LOGIC ---
    // This block now handles both cases: before and after 10 AM.
    // The field's value is set programmatically and the field is always disabled.
    if (now > deadline) {
      // If it's after 10 AM, default to "No".
      form.setFieldsValue({ IsOpenBy10AM: false });
    } else {
      // If it's before 10 AM, automatically select "Yes".
      form.setFieldsValue({ IsOpenBy10AM: true });
    }
    // --- END OF UPDATE ---
  }, [form]);

  const fetchBranches = async () => {
    try {
      const response = await fetch(
        `${Api_Url}api/v1/Branch?PageNumber=1&PageSize=1000`,
        {
          headers: {
            Authorization: `Bearer ${
              JSON.parse(sessionStorage.getItem("user")).accessToken
            }`,
          },
        }
      );
      const data = await response.json();
      setBranches(data.data);
    } catch (error) {
      message.error("Failed to load branches");
    }
  };

  const getGeolocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          });
        },
        (error) => {
          message.warning(
            "Geolocation permission denied. Location will not be recorded."
          );
        }
      );
    }
  };

  const handleShutterImageUpload = (info) => {
    if (info.file.status === "removed") {
      setShutterImage(null);
      return;
    }
    const file = info.file.originFileObj || info.file;
    setShutterImage(file);
  };

  const handleCboApprovalImageUpload = (info) => {
    if (info.file.status === "removed") {
      setCboApprovalImage(null);
      return;
    }
    const file = info.file.originFileObj || info.file;
    setCboApprovalImage(file);
  };

  const onFinish = async (values) => {
    if (!shutterImage) {
      message.error("Please upload the shutter/main door image");
      return;
    }
    
    if (values.IsOpenBy10AM === false) {
      if (values.HasCBOApproval === null || values.HasCBOApproval === undefined) {
        message.error("Please specify if you received CBO approval.");
        return;
      }
      if (!values.ReasonForDelay) {
        message.error("Please provide a reason for the delay.");
        return;
      }
      if (values.HasCBOApproval === true && !cboApprovalImage) {
        message.error("Please upload the CBO approval image.");
        return;
      }
    }

    setLoading(true);
    const formData = new FormData();

    // Append all form data
    formData.append("BranchId", values.BranchId);
    formData.append("ShutterImage", shutterImage);
    formData.append("Latitude", location.lat || "");
    formData.append("Longitude", location.lng || "");

    if (values.IsOpenBy10AM !== undefined) {
      formData.append("IsOpenBy10AM", values.IsOpenBy10AM);
    }

    if (values.IsOpenBy10AM === false) {
      formData.append("ReasonForDelay", values.ReasonForDelay || "");
      formData.append("HasCBOApproval", values.HasCBOApproval);
      if (values.HasCBOApproval === true && cboApprovalImage) {
        formData.append("CBOApprovalImage", cboApprovalImage);
      }
    }

    try {
      const response = await fetch(`${Api_Url}api/v1/BranchOpening`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${
            JSON.parse(sessionStorage.getItem("user")).accessToken
          }`,
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Submission failed");
      }

      message.success("Branch opening report submitted successfully");
      if (onSuccess) onSuccess();
      onClose();
    } catch (error) {
      message.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={
        <span style={{ fontSize: "20px", fontWeight: "bold" }}>
          Add Branch Opening Report
        </span>
      }
      width={700}
      visible={true}
      onCancel={onClose}
      footer={[
        <Space key="footer-buttons" size="large">
          <Button key="back" onClick={onClose} size="large" style={{ width: 120 }}>
            Cancel
          </Button>
          <Button key="submit" type="primary" loading={loading} onClick={() => form.submit()} size="large" style={{ width: 120 }}>
            Submit
          </Button>
        </Space>,
      ]}
      centered
      bodyStyle={{ padding: "24px" }}
    >
      <Form form={form} layout="vertical" onFinish={onFinish}>
        <Row gutter={24}>
          <Col span={12}>
            <Form.Item
              name="BranchId"
              label={<span style={{ fontWeight: 500 }}>Branch</span>}
              rules={[{ required: true, message: "Please select a branch" }]}
            >
              <Select
                placeholder="Select branch"
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) =>
                  option.children.toLowerCase().includes(input.toLowerCase())
                }
                size="large"
              >
                {branches.map((branch) => (
                  <Option key={branch.id} value={branch.id}>
                    {branch.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="IsOpenBy10AM"
              label={
                <span style={{ fontWeight: 500 }}>Branch Opened by 10 AM?</span>
              }
              rules={[{ required: true, message: "This field is required" }]}
            >
              <Select
                placeholder="Select Yes or No"
                size="large"
                disabled={true} // --- UPDATED: This field is now always disabled ---
              >
                <Option value={true}>Yes</Option>
                <Option value={false}>No</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          noStyle
          shouldUpdate={(prevValues, currentValues) =>
            prevValues.IsOpenBy10AM !== currentValues.IsOpenBy10AM
          }
        >
          {({ getFieldValue }) =>
            getFieldValue("IsOpenBy10AM") === false && (
              <>
                <Divider orientation="left">Late Opening Details</Divider>
                <Row gutter={24}>
                  <Col span={24}>
                    <Form.Item
                      name="HasCBOApproval"
                      label={
                        <span style={{ fontWeight: 500 }}>
                          CBO Approval Received?
                        </span>
                      }
                      rules={[
                        { required: true, message: "This field is required" },
                      ]}
                    >
                      <Select placeholder="Select Yes or No" size="large">
                        <Option value={true}>Yes</Option>
                        <Option value={false}>No</Option>
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>
                
                <Row gutter={24}>
                  <Col span={24}>
                    <Form.Item
                      name="ReasonForDelay"
                      label={
                        <span style={{ fontWeight: 500 }}>
                          Reason for Delay / Remarks
                        </span>
                      }
                      rules={[
                        {
                          required: true,
                          message: "Please enter the reason for the delay",
                        },
                      ]}
                    >
                      <TextArea
                        rows={3}
                        placeholder="Explain why the branch was not opened on time"
                        size="large"
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item
                  noStyle
                  shouldUpdate={(prevValues, currentValues) =>
                    prevValues.HasCBOApproval !== currentValues.HasCBOApproval
                  }
                >
                  {({ getFieldValue }) =>
                    getFieldValue("HasCBOApproval") === true && (
                      <Row gutter={24}>
                        <Col span={24}>
                          <Form.Item
                            label={
                              <span style={{ fontWeight: 500 }}>
                                Upload CBO Approval
                              </span>
                            }
                            required
                          >
                            <Upload
                              accept="image/*"
                              listType="picture-card"
                              showUploadList={false}
                              beforeUpload={() => false}
                              onChange={handleCboApprovalImageUpload}
                            >
                              {cboApprovalImage ? (
                                <img
                                  src={URL.createObjectURL(cboApprovalImage)}
                                  alt="approval"
                                  style={{ width: "100%", objectFit: "cover" }}
                                />
                              ) : (
                                <div>
                                  <CameraOutlined />
                                  <div style={{ marginTop: 8 }}>Upload Image</div>
                                </div>
                              )}
                            </Upload>
                          </Form.Item>
                        </Col>
                      </Row>
                    )
                  }
                </Form.Item>
              </> 
            )
          }
        </Form.Item>

        <Divider orientation="left">Documentation</Divider>
        <Row gutter={24}>
          <Col span={24}>
            <Form.Item
              label={
                <span style={{ fontWeight: 500 }}>
                  Shutter/Main Door Image
                </span>
              }
              required
            >
              <Upload
                accept="image/*"
                listType="picture-card"
                showUploadList={false}
                beforeUpload={() => false}
                onChange={handleShutterImageUpload}
              >
                {shutterImage ? (
                  <img
                    src={URL.createObjectURL(shutterImage)}
                    alt="shutter"
                    style={{ width: "100%", objectFit: "cover" }}
                  />
                ) : (
                  <div>
                    <CameraOutlined />
                    <div style={{ marginTop: 8 }}>Upload Image</div>
                  </div>
                )}
              </Upload>
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};